/**
 * 417 (#1464, US3, mock-up frame 4): the customer's mileage package on their booking page, from `mileage_use`.
 * Package 2 shows the km used of its allowance, the km left and, once over, what may be charged; Package 1 states the
 * package and, once measured (425), the km driven and the km outside. Never a charge: staff decide that at the end.
 */
export type MileageUse =
  | {
      package: 1;
      rate_minor_per_km: number;
      km_used?: number | null;
      km_outside?: number | null;
      final?: boolean;
    }
  | {
      package: 2;
      km_used: number | null;
      allowance_km: number | null;
      over_km: number | null;
      rate_minor_per_km: number;
    };

const km = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1));
const rate = (minor: number) => `RM ${(minor / 100).toFixed(2)}/km`;

export function MileageUseCard({ use }: { use: MileageUse }) {
  if (use.package === 1) {
    const driven = use.km_used ?? 0;
    const outside = use.km_outside ?? 0;
    return (
      <div
        data-testid="mileage-use"
        className="mt-4 flex flex-col gap-2 rounded-xl bg-surface-card px-4 py-3"
      >
        <p className="text-sm font-semibold text-text">Package 1 · Stay in the area</p>
        <p className="text-xs text-text-muted">
          Unlimited km inside the area · outside: {rate(use.rate_minor_per_km)}
        </p>
        {driven > 0 && (
          <p data-testid="mileage-driven" className="text-sm text-text">
            {km(driven)} km driven{use.final ? '' : ' so far'}
          </p>
        )}
        {outside > 0 && (
          <p data-testid="mileage-outside" className="text-sm text-warning">
            {km(outside)} km outside the area · may be charged {rate(use.rate_minor_per_km)}
          </p>
        )}
      </div>
    );
  }
  const allowance = use.allowance_km ?? 0;
  const over = (use.over_km ?? 0) > 0;
  return (
    <div
      data-testid="mileage-use"
      className="mt-4 flex flex-col gap-2 rounded-xl bg-surface-card px-4 py-3"
    >
      <p className="text-sm font-semibold text-text">Package 2 · Go anywhere</p>
      <p data-testid="mileage-used" className="text-sm text-text">
        {use.km_used === null
          ? `${allowance} km included`
          : `${km(use.km_used)} of ${allowance} km used`}
      </p>
      {use.km_used !== null && allowance > 0 && (
        <progress
          aria-label="Km used"
          max={allowance}
          value={Math.min(use.km_used, allowance)}
          className={`h-2 w-full appearance-none overflow-hidden rounded-full [&::-webkit-progress-bar]:bg-surface-mid ${
            over
              ? '[&::-moz-progress-bar]:bg-warning [&::-webkit-progress-value]:bg-warning'
              : '[&::-moz-progress-bar]:bg-primary [&::-webkit-progress-value]:bg-primary'
          }`}
        />
      )}
      {use.km_used !== null && allowance > 0 && use.km_used <= allowance && (
        <p data-testid="mileage-left" className="text-sm text-text">
          {km(allowance - use.km_used)} km left
        </p>
      )}
      {over && (
        <p data-testid="mileage-over" className="text-sm text-warning">
          Over by {km(use.over_km as number)} km · may be charged {rate(use.rate_minor_per_km)}
        </p>
      )}
    </div>
  );
}
